import type { ConfigThemeFontDefinition } from '@mfe/config';
import { config } from '@mfe/config';
import type { State } from '@mfe/lib/store';
import { TriggerEventMixin } from '@mfe/lit/mixins/TriggerEventMixin';
import type { TemplateTheme } from '@mfe/lit/TemplateResolver';
import { configStore, StateController } from '@mfe/store';
import { assert } from '@mfe/util/assert';
import { LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import normalizeUrl from 'normalize-url';

import { TemplatingMixin } from './mixins/TemplatingMixin';

interface StateControllerList {
  [key: string] : StateController<State>;
}

export class LitElementComs extends TemplatingMixin(TriggerEventMixin(LitElement)) {
  // See https://www.typescriptlang.org/docs/handbook/2/classes.html#index-signatures
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string | symbol]: any;

  // These properties are controllable from the outside via HTML attributes eg. on the components tag
  @property({ type: String, attribute: 'theme-base-url' }) themeBaseUrl! : string;
  @property({ type: String, attribute: 'theme' }) theme? : TemplateTheme = 'occl';
  @property({ type: String, attribute: 'access-token' }) accessToken? : string | undefined;
  @property({ type: String, attribute: 'traceid' }) traceid? : string | undefined;

  // By default the complete component is invisible until all resources are loaded.
  // To "reserve" the space look up the approximate height of the finished rendered component (via devtools)
  // and set the corresponding CSS var in TefComs4cs4p.css
  @state() cloak : boolean = true;

  // Tracks the number of loaded resources
  @state() loadedResourcesCount : number = 0;
  @state() totalResourcesCount : number = 0;

  stateController : StateControllerList = {};
  #errorEventTriggered : boolean = false;

  static version : string = _MFE_VERSION; // Filled/replaced by esbuild (see scripts/esbuild/*.ts)

  constructor() {
    super();
    this.setupStore(configStore);
  }

  override connectedCallback() {
    // Required attributes must be present on the element:
    assert(!!this.themeBaseUrl, 'missing required html attribute "theme-base-url"');
    assert(Object.keys(config.theme).includes(this.theme!), `html attribute "theme" must be one of ["${Object.keys(config.theme).join('","')}"]`);

    // Call super only AFTER validating all required attributes!
    super.connectedCallback();

    // @ts-expect-error "Don't know how to type this"
    this.updateConfigStoreValues(this.constructor.elementProperties as Map<PropertyKey, unknown>);
    this.loadThemeResources();
  }

  setupStore(store : State) {
    const storeName = store.constructor.name.substring(0, 1).toLowerCase() + store.constructor.name.substring(1);
    // Reset store just to be sure
    store.reset();

    // Keep reference to statecontroller
    this.stateController[storeName] = new StateController(this, store);
  }

  // Update the config store with all changed @property
  updateConfigStoreValues(changedProperties: Map<PropertyKey, unknown>) {
    changedProperties.forEach((_value : unknown, key : PropertyKey) => {
      // Write known key/values to configStore
      if (configStore.propertyMap.has(key) && typeof this[key] !== 'undefined') {
        configStore[key] = this[key];
      }
    });
  }

  // Load all configured resources into the component
  // See src/config/index.ts
  loadThemeResources() {
    // Only activate if both values are known
    if (configStore.theme && configStore.themeBaseUrl) {
      const themeConfig = config.theme[configStore.theme];
      if (!themeConfig) return;

      const fonts = themeConfig.fonts || [];
      this.totalResourcesCount = themeConfig.resources.length + fonts.length;
      this.loadResources(themeConfig.resources.map((r) => r.path), fonts);
    }
  }

  // Counts the loaded resources and uncloaks the compoennt if all is loaded
  handleResourceLoaded() {
    this.loadedResourcesCount += 1;
    // Loaded all resources -> uncloak component
    if (this.loadedResourcesCount >= this.totalResourcesCount) {
      this.cloak = false;
    }
  }

  // eport an error if resource cannot load
  handleResourceError(event : Event) {
    if (this.#errorEventTriggered) return;

    const resource = (event.target! as HTMLElement).getAttribute('src') || (event.target! as HTMLElement).getAttribute('href');

    this.triggerEvent({
      name: 'load/ERROR', level: 'error', message: `Loading of resource "${resource}" failed`,
    });
    this.#errorEventTriggered = true;
  }

  // Load all resources for the theme (occl, tefOne) and injects them inside the component's shadow root.
  loadResources(resources : string[], fonts : ConfigThemeFontDefinition[] = []) {
    let $element : HTMLElement;

    // Trigger font loading
    // eslint-disable-next-line no-void
    void this.loadFontsIntoLightDom(fonts);

    resources.forEach((resource) => {
      const resourceTypeMatch = resource.match(/\.(?<resourceType>(?:css|js))(?:$|\?|#)/);
      if (!resourceTypeMatch) return;
      const { resourceType } = resourceTypeMatch.groups as { resourceType: string }; // This is why I don't like all this cruft needed by TS.
      const resourceUrl = normalizeUrl(`${configStore.themeBaseUrl}/${resource}`);

      if (resourceType === 'js') {
        $element = document.createElement('script');
        $element.setAttribute('src', resourceUrl);
        $element.setAttribute('type', 'module');
      }
      if (resourceType === 'css') {
        $element = document.createElement('link');
        $element.setAttribute('rel', 'stylesheet');
        $element.setAttribute('href', resourceUrl);
        $element.setAttribute('as', 'style');
      }

      $element.setAttribute('fetchpriority', 'high');
      $element.addEventListener('load', () => { this.handleResourceLoaded(); });
      $element.addEventListener('error', (e) => { (this.handleResourceError.bind(this) as CallableFunction)(e); });

      this.renderRoot.appendChild($element);
    });
  }

  // Load all necessary fonts into the web components
  // See https://developer.mozilla.org/en-US/docs/Web/API/CSS_Font_Loading_API
  async loadFontsIntoLightDom(fontDefinitions : ConfigThemeFontDefinition[]) {
    // Clean up the base url since it already contains the brandId in the URL.
    // https://dev-library.telefonica.de/1 => https://dev-library.telefonica.de/static/1/...
    const baseUrl = configStore.themeBaseUrl.replace(/\/(\d+)$/, '/static/$1');

    const fontFaces = fontDefinitions.map((fontDefinition) => {
      const attributes = (({ src, ...rest }) => rest)(fontDefinition);
      return new FontFace(fontDefinition.family, `url(${baseUrl}${fontDefinition.src})`, attributes);
    });

    await Promise.all(fontFaces.map((fontFace) => fontFace.load().then(() => { this.handleResourceLoaded(); })));
    fontFaces.forEach((fontFace) => document.fonts.add(fontFace));
  }
}
